import React, { useState, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

/**
 * PasscodeGuard Props
 */
interface PasscodeGuardProps {
  /**
   * Child components to render when authenticated
   */
  children: React.ReactNode;
}

/**
 * PasscodeGuard Component
 * 
 * A guard component that displays a passcode entry form when the user is not authenticated.
 * Once authenticated, it renders the children components.
 * 
 * This component must be used within an AuthProvider to access authentication state.
 * The passcode is verified against the VITE_KXD_ACCESS_PASSCODE environment variable.
 * 
 * ## Design Decision: Tailwind CSS vs MUI
 * 
 * This component intentionally uses **Tailwind CSS** instead of MUI components with the `sx` prop.
 * Rationale:
 * - This is a standalone authentication guard that appears BEFORE the main application loads
 * - Using Tailwind reduces bundle size by avoiding MUI dependencies for this single screen
 * - The passcode screen is a simple, one-time-use interface with minimal interactivity
 * - Tailwind provides better performance for this isolated use case
 * - The design is based on the original implementation requirements
 * 
 * For all application components AFTER authentication, continue using MUI with `sx` prop
 * as per project standards.
 * 
 * @example
 * // Basic usage (must be inside AuthProvider)
 * <AuthProvider>
 *   <PasscodeGuard>
 *     <App />
 *   </PasscodeGuard>
 * </AuthProvider>
 * 
 * @example
 * // In app's main.tsx
 * import { AuthProvider, PasscodeGuard } from '@kxd/ui';
 * 
 * createRoot(document.getElementById('root')!).render(
 *   <ThemeProvider theme={theme}>
 *     <CssBaseline />
 *     <AuthProvider>
 *       <PasscodeGuard>
 *         <App />
 *       </PasscodeGuard>
 *     </AuthProvider>
 *   </ThemeProvider>
 * );
 */
const PasscodeGuard: React.FC<PasscodeGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, authenticate } = useAuth();
  const [passcode, setPasscode] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Clear passcode field when user logs out (isAuthenticated becomes false)
  React.useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      setPasscode('');
      setError('');
    }
  }, [isAuthenticated, authLoading]);

  /**
   * Handle passcode form submission
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await authService.login(passcode);
      
      if (result.success) {
        authenticate();
      } else {
        setError(result.message || 'Invalid passcode. Please try again.');
        setPasscode('');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Authentication failed. Please try again.');
      setPasscode('');
    } finally {
      setIsLoading(false);
    }
  };

  // Show nothing while loading authentication state from localStorage
  if (authLoading) {
    return null;
  }

  // If authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Otherwise, render passcode entry form
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img 
            src="https://branding.konnect3d.com/clients/k3d-demo/kxd-logo-nav.svg" 
            alt="K3D Logo" 
            width="219" 
            height="40"
            className="h-12"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Enter Access Code
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Please enter your passcode to access the application
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="passcode" className="block text-sm font-medium text-gray-700">
                Passcode
              </label>
              <div className="mt-1">
                <input
                  id="passcode"
                  name="passcode"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="Enter your passcode"
                  disabled={isLoading}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading || !passcode}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-blue-400 transition-colors duration-200"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verifying...
                  </>
                ) : (
                  'Access Application'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Contact your administrator if you need access
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasscodeGuard;
